import axiosInstance from '..';
import store from '~/redux/store';
import {ApiRoute} from '~/constants/api';

export interface IInvoiceItem {
  id: number;
  description: string;
  notes: string | null;
  ccsd_code: string | null;
  amount: number;
  category: string;
  item_name: string;
}

export interface IInvoiceDetails {
  id: number;
  invoice_number: string;
  issued_at: string;
  invoice_issued_at: string | null;
  payment?: {
    id: number;
    amount: number;
    patient_id: number;
    status: boolean;
    payment_method: string;
    patient_full_name: string;
    invoice_number: string;
    description: string;
    billing_date: string;
    items: IInvoiceItem[];
  };
  insurance_claim?: {
    id: number;
    amount: number;
    patient_id: number;
    status: string;
    membership_no: string;
    pre_authorisation_code: string;
    insurer_name: string;
    patient_full_name: string;
    invoice_number: string;
    description: string;
    billing_date: string;
    items: IInvoiceItem[];
  };
  patient: {
    id: number;
    full_name: string;
    uniq_code: string;
    mobile_number: string;
    dob: string;
    email: string;
    profile_url: string;
    address: {
      id: number;
      line1: string;
      line2: string;
      city: string;
      state: string | null;
      country: string;
      postcode: string;
      country_name: string;
      city_name: string;
    };
  };
  consultant_name: string;
  consultant: {
    id: number;
    full_name: string;
  };
}

export interface ICreateInvoiceVariables {
  payment_id?: number;
  insurance_claim_id?: number;
}

export interface ICreateInvoicePayload {
  invoice: {
    payment_id?: number;
    insurance_claim_id?: number;
  };
}

export interface ICreateInvoiceResponseData {
  data: {invoice: IInvoiceDetails};
  message: string;
}

export const postCreateInvoice = async (variables: ICreateInvoiceVariables) => {
  const state = store.getState();
  const token = state.auth.token;
  const {payment_id, insurance_claim_id} = variables;

  const payload: ICreateInvoicePayload = {
    invoice: {},
  };

  if (payment_id) {
    payload.invoice.payment_id = payment_id;
  }

  if (insurance_claim_id) {
    payload.invoice.insurance_claim_id = insurance_claim_id;
  }

  const response = await axiosInstance.post<ICreateInvoiceResponseData>(
    ApiRoute.Invoice,
    payload,
    {
      headers: {
        Authorization: token,
      },
    }
  );

  return response.data;
};

export interface IGenerateInvoiceVariables {
  invoice_id: number;
}

export type IPostGenerateInvoiceResponseData = Blob;

export const postGenerateInvoice = async (
  variables: IGenerateInvoiceVariables
) => {
  const state = store.getState();
  const token = state.auth.token;
  const {invoice_id} = variables;

  const response = await axiosInstance.post<IPostGenerateInvoiceResponseData>(
    ApiRoute.GenerateInvoice,
    {
      invoice: {
        invoice_id,
      },
    },
    {
      headers: {
        Authorization: token,
      },
      responseType: 'blob',
    }
  );

  return response.data;
};

export type PostGenerateReceiptResponseData = Blob;
export interface generateReceiptVariables {
  payment_id: number;
}

export const postGenerateReceipt = async (
  variables: generateReceiptVariables
) => {
  const state = store.getState();
  const token = state.auth.token;
  const {payment_id} = variables;

  const response = await axiosInstance.post<PostGenerateReceiptResponseData>(
    ApiRoute.GenerateReceipt,
    {
      receipt: {
        payment_id,
      },
    },
    {
      headers: {
        Authorization: token,
      },
      responseType: 'blob',
    }
  );

  return response.data;
};

export interface IUpdateInvoiceVariables {
  invoice_id: number;
  consultant_id: number;
}

export const postUpdateInvoice = async (variables: IUpdateInvoiceVariables) => {
  const state = store.getState();
  const token = state.auth.token;
  const {invoice_id, consultant_id} = variables;

  const response = await axiosInstance.put(
    `${ApiRoute.Invoice}/${invoice_id}`,
    {
      invoice: {consultant_id},
    },
    {
      headers: {Authorization: token},
    }
  );
  return response.data;
};

export interface ISaveInvoiceVariables {
  invoice_id: number;
}

export interface InvoiceResponseData {
  id: number;
  invoice_number: string;
  issued_at: string;
  patient: {
    id: number;
    full_name: string;
    uniq_code: string;
    mobile_number: string;
    email: string;
    profile_url: string;
    address: {
      id: number;
      line1: string;
      line2: string;
      city: string;
      state: string | null;
      country: string;
      postcode: string;
    };
    dob: string;
  };
  payment: {
    id: number;
    amount: number;
    patient_id: number;
    description: string;
    date_of_payment: string;
    items: Array<{
      id: number;
      description: string;
      notes: string | null;
      amount: number;
      category: string;
      item_name: string;
    }>;
    status: string;
  };
  insurance_claim: {
    id: number;
    membership_no: string;
    pre_authorisation_code: number;
    claimed_amount: number;
    approved_amount: number | null;
    status: string;
    insurer_name: string;
  };
  consultant_name: string;
}

export interface ISaveInvoiceResponseData {
  success: boolean;
  data: {
    invoice: InvoiceResponseData;
  };
  message: string;
}

export const patchSaveInvoice = async (variables: ISaveInvoiceVariables) => {
  const state = store.getState();
  const token = state.auth.token;
  const {invoice_id} = variables;

  const response = await axiosInstance.patch<ISaveInvoiceResponseData>(
    `${ApiRoute.Invoice}/${invoice_id}`,
    {
      invoice: {
        issued_at: new Date().toISOString(),
      },
    },
    {
      headers: {
        Authorization: token,
      },
    }
  );

  return response.data;
};
export interface ISendReceiptResponse {
  success: boolean;
  data: [];
  message: string;
}

export const getSendReceipt = async (paymentId: number) => {
  const state = store.getState();
  const token = state.auth.token;

  const response = await axiosInstance.get<ISendReceiptResponse>(
    `${ApiRoute.Payments}/${paymentId}/send_receipt`,
    {
      headers: {
        Authorization: token,
      },
    }
  );

  return response.data;
};
